import React, { useCallback, useEffect, useState } from "react";
import { Loader2, KeyRound, RefreshCw, CheckCircle2, XCircle } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../../lib/api";
import { apiErr } from "../../../lib/apiErr";
import { FallbackSwitch } from "./FallbackSwitch";
import { CAP_LABEL, fmtTime, provName } from "./llmUtil";

function SourceBadge({ c }) {
  if (c.effective_source === "unavailable") return <span className="rounded-full bg-rose-50 px-2 py-0.5 text-[11px] font-bold text-rose-700">Tidak tersedia</span>;
  if (c.last_source === "emergent") return <span className="rounded-full bg-amber-50 px-2 py-0.5 text-[11px] font-bold text-amber-700">Cadangan Emergent</span>;
  if (c.last_source === "own") return <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-[11px] font-bold text-emerald-700">Key sendiri</span>;
  return <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-semibold text-slate-500">Belum dipakai</span>;
}

export default function KeyStatusTab() {
  const [d, setD] = useState(null);
  const load = useCallback(() => api.get("/admin/llm/key-status").then((r) => setD(r.data)).catch((e) => { toast.error(apiErr(e, "Gagal memuat status key")); setD({ providers: [], capabilities: [] }); }), []);
  useEffect(() => { load(); }, [load]);
  if (!d) return <Loader2 className="animate-spin text-slate-400" />;
  return (
    <div className="space-y-5" data-testid="llm-keys">
      <FallbackSwitch value={!!d.emergent_fallback} onChanged={(s) => setD((x) => ({ ...x, emergent_fallback: s.emergent_fallback }))} />
      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        {(d.providers || []).map((p) => (
          <div key={p.provider} className="aivora-card flex items-center gap-3 p-4" data-testid={`llm-provider-${p.provider}`}>
            <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-slate-100 text-slate-600"><KeyRound size={16} /></span>
            <div className="min-w-0 flex-1"><p className="text-sm font-bold text-slate-900">{provName(p.provider)}</p>
              <p className={`flex items-center gap-1 text-[11px] font-semibold ${p.own_key_configured ? "text-emerald-600" : "text-slate-400"}`}>{p.own_key_configured ? <CheckCircle2 size={12} /> : <XCircle size={12} />}{p.own_key_configured ? "Key sendiri terpasang" : "Tanpa key sendiri"}</p></div>
          </div>
        ))}
      </div>
      <div className="aivora-card overflow-x-auto">
        <div className="flex items-center justify-between px-4 pt-4">
          <div><p className="text-sm font-bold text-slate-900">Sumber key per kapabilitas</p><p className="text-[11px] text-slate-400 sm:hidden">Geser tabel ke samping untuk kolom lain →</p></div>
          <button onClick={load} className="btn-soft inline-flex items-center gap-1.5 rounded-xl px-3 py-1.5 text-xs font-bold" data-testid="llm-keys-refresh"><RefreshCw size={13} /> Segarkan</button>
        </div>
        <table className="tbl mt-3 [&_td]:px-4 [&_td]:py-3 [&_td]:align-top [&_th]:px-4 [&_th]:py-2.5 [&_th]:text-left">
          <thead><tr><th>Kapabilitas</th><th>Provider</th><th>Status</th><th>Model terakhir</th><th>Terakhir dipakai</th><th className="!text-right">Panggilan bulan ini</th></tr></thead>
          <tbody>
            {(d.capabilities || []).map((c) => (
              <tr key={`${c.capability}:${c.provider}`} data-testid={`llm-cap-${c.capability}-${c.provider}`}>
                <td className="font-semibold text-slate-800">{c.label || CAP_LABEL[c.capability] || c.capability}</td>
                <td>{provName(c.provider)}</td>
                <td>
                  <div className="flex flex-col items-start gap-1" title={c.last_error || ""}>
                    <SourceBadge c={c} />
                    {c.last_error && <span className="max-w-[260px] truncate text-[11px] text-rose-600" data-testid={`llm-cap-error-${c.capability}-${c.provider}`}>{c.last_error} · {fmtTime(c.last_error_at)}</span>}
                  </div>
                </td>
                <td className="font-mono text-xs text-slate-600">{c.last_model || "—"}</td>
                <td className="text-xs text-slate-500">{fmtTime(c.last_used_at)}{c.last_fallback_at && <span className="block text-amber-600">fallback {fmtTime(c.last_fallback_at)}</span>}</td>
                <td className="text-right text-xs"><b className="text-slate-800">{c.month_calls_own}</b> sendiri · <b className="text-amber-700">{c.month_calls_emergent}</b> Emergent</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
